import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowRight, Sparkles } from "lucide-react";
import api from "../../lib/api";
import { usePublic } from "../../components/PublicLayout";
import { Reveal } from "../../components/Reveal";
import {
  ServicesSection, HowItWorksSection, PortfolioSection, WhySection,
  PricingSection, TestimonialsSection, FaqSection, FinalCta, VideoModal,
} from "../../components/sections";

function AnimatedStat({ value, label, delay }) {
  return (
    <Reveal delay={delay}>
      <div className="text-center sm:text-left">
        <div className="font-heading font-extrabold text-4xl sm:text-5xl text-brand-purple">{value}</div>
        <div className="mt-1 text-sm font-medium text-brand-purpleDeep/60">{label}</div>
      </div>
    </Reveal>
  );
}

export default function Home() {
  const { settings } = usePublic();
  const navigate = useNavigate();
  const [data, setData] = useState({ services: [], portfolio: [], testimonials: [], pricing: [], faqs: [], logos: [], addons: [] });
  const [featOpen, setFeatOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/content/services"), api.get("/content/portfolio"), api.get("/content/testimonials"),
      api.get("/content/pricing"), api.get("/content/faqs"), api.get("/content/client-logos"), api.get("/content/addons"),
    ]).then(([s, p, t, pr, f, l, a]) => {
      setData({ services: s.data, portfolio: p.data, testimonials: t.data, pricing: pr.data, faqs: f.data, logos: l.data, addons: a.data });
    }).catch(() => {});
  }, []);

  const heroThumb = data.portfolio[0]?.thumbnail_url;

  return (
    <>
      {/* HERO */}
      <section className="relative pt-28 sm:pt-36 pb-20 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-40 -left-40 w-[36rem] h-[36rem] rounded-full bg-brand-light blur-3xl opacity-70" />
          <div className="absolute top-20 right-0 w-[28rem] h-[28rem] rounded-full bg-brand-orangeLight blur-3xl opacity-60" />
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <motion.span
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-white border border-border rounded-full px-4 py-1.5 text-sm font-medium text-brand-purple shadow-sm"
            >
              <Sparkles size={15} className="text-brand-orange" /> Fixed packages. Fast delivery.
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-6 font-heading font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-[0.98] text-brand-purpleDeep"
            >
              {settings.hero_title || "Professional video content made easy."}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
              className="mt-6 text-lg text-brand-purpleDeep/60 max-w-lg leading-relaxed"
            >
              {settings.hero_subtitle}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <button
                onClick={() => navigate("/contact")}
                className="group inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangeDark text-white font-semibold px-7 py-4 rounded-lg transition-all hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(255,101,0,0.3)]"
                data-testid="hero-start-video"
              >
                Get Started <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => navigate("/pricing")}
                className="inline-flex items-center gap-2 bg-white border border-border hover:border-brand-purple text-brand-purpleDeep font-semibold px-7 py-4 rounded-lg transition-colors"
                data-testid="hero-explore-packages"
              >
                View Packages
              </button>
            </motion.div>
          </div>

          {/* Animated media area */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="relative"
          >
            <div className="relative aspect-[4/3.2] rounded-3xl overflow-hidden bg-brand-purpleDeep shadow-[0_30px_80px_rgba(130,26,200,0.25)]">
              {heroThumb && <img src={heroThumb} alt="Showreel" className="absolute inset-0 w-full h-full object-cover opacity-70" />}
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-purple/60 via-transparent to-brand-orange/20" />
              <button
                onClick={() => setFeatOpen(true)}
                className="absolute inset-0 flex items-center justify-center group"
                data-testid="hero-play"
              >
                <span className="relative flex items-center justify-center">
                  <span className="absolute w-24 h-24 rounded-full bg-brand-orange/30 animate-ping" />
                  <span className="w-20 h-20 rounded-full bg-brand-orange flex items-center justify-center text-white shadow-xl group-hover:scale-105 transition-transform">
                    <Play size={30} className="ml-1" fill="currentColor" />
                  </span>
                </span>
              </button>
              <div className="absolute bottom-5 left-5 glass rounded-xl px-4 py-3 text-white">
                <div className="text-xs text-white/70">Watch our showreel</div>
                <div className="font-heading font-bold">See what easy looks like</div>
              </div>
            </div>
            <motion.div
              animate={{ y: [0, -10, 0] }} transition={{ duration: 4, repeat: Infinity }}
              className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl border border-border p-4 hidden sm:block"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-light flex items-center justify-center text-brand-purple font-heading font-bold">✓</div>
                <div>
                  <div className="font-heading font-bold text-sm text-brand-purpleDeep">Concept to final cut</div>
                  <div className="text-xs text-brand-purpleDeep/50">All handled for you</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* PACKAGES — moved high up */}
      <PricingSection packages={data.pricing} addons={data.addons} settings={settings} hideAddons />

      <ServicesSection services={data.services} />
      <HowItWorksSection />
      <PortfolioSection projects={data.portfolio} limit={6} />

      {/* FEATURED VIDEO */}
      <section className="py-24 px-5 sm:px-8" data-testid="featured-video-section">
        <div className="max-w-6xl mx-auto rounded-3xl bg-gradient-to-b from-[#13041C] to-[#0A020F] overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-0 items-center">
            <div className="p-10 sm:p-14">
              <span className="text-brand-orange text-xs font-bold uppercase tracking-[0.16em]">Featured</span>
              <h2 className="mt-4 font-heading font-extrabold text-3xl sm:text-4xl text-white leading-tight">
                {settings.featured_title || "Your business deserves better video."}
              </h2>
              <p className="mt-4 text-white/60 leading-relaxed">
                See how we take businesses from a rough idea to a finished video they're proud to share.
              </p>
              <button
                onClick={() => navigate("/contact")}
                className="mt-7 inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangeDark text-white font-semibold px-7 py-3.5 rounded-lg transition-all hover:-translate-y-0.5"
                data-testid="featured-cta"
              >
                Let's make something <ArrowRight size={17} />
              </button>
            </div>
            <div className="p-6 lg:p-10">
              <button
                onClick={() => setFeatOpen(true)}
                className="group relative w-full aspect-video rounded-2xl overflow-hidden bg-black"
                data-testid="featured-play"
              >
                {heroThumb && <img src={heroThumb} alt="Featured" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-75 transition-opacity" />}
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-16 h-16 rounded-full bg-brand-orange flex items-center justify-center text-white group-hover:scale-105 transition-transform shadow-xl">
                    <Play size={26} className="ml-1" fill="currentColor" />
                  </span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <WhySection />
      <TestimonialsSection testimonials={data.testimonials} />
      <FaqSection faqs={data.faqs} />
      <FinalCta settings={settings} />

      <VideoModal
        open={featOpen}
        onClose={() => setFeatOpen(false)}
        video={{ title: "EasyVideo Showreel", category: "Showreel", video_url: settings.featured_video_url, description: "A taste of what we make." }}
      />
    </>
  );
}
